// Configuración → Plantillas y letras (§1.5): las dos plantillas con su estado real en
// la nube (P-22), las letras y los ajustes finos de posición de los carteles.
import { Fragment, useEffect, useId, useRef, useState, type DragEvent } from "react";
import { ChevronDown, CloudCheck, CloudOff, FileText, Upload } from "lucide-react";
import { Button, Card, Field, ICON, Input, Modal, Seg, Skeleton, Spinner, useToast } from "./ui";
import { DEFAULT_PRESET, type PDFPreset } from "./carteles/generar";
import { cargarPreset, guardarPreset } from "./carteles/preset";
import { leerMetaPlantilla, motivoDeFalla, subirPlantilla, type Orientacion, type PlantillaMeta } from "./carteles/plantillasApi";

const MON3 = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const ORIENTACIONES: { value: Orientacion; label: string }[] = [
  { value: "horizontal", label: "Horizontal" },
  { value: "vertical", label: "Vertical" },
];

interface Ajuste { clave: keyof PDFPreset; etiqueta: string; min: number; max: number }

// Mismos rangos que tenía la pantalla vieja de carteles
const AJUSTES: Record<Orientacion, Ajuste[]> = {
  horizontal: [
    { clave: "startX", etiqueta: "Margen izquierdo", min: 10, max: 150 },
    { clave: "colWidth", etiqueta: "Ancho de columna", min: 100, max: 260 },
    { clave: "colGap", etiqueta: "Separación de columnas", min: -20, max: 80 },
    { clave: "headerBoxBottom", etiqueta: "Altura de los días", min: 250, max: 500 },
    { clave: "headerBoxHeight", etiqueta: "Alto del encabezado", min: 10, max: 100 },
    { clave: "pharmacyBoxBottom", etiqueta: "Altura de las farmacias", min: 50, max: 250 },
    { clave: "pharmacyBoxHeight", etiqueta: "Alto del recuadro de farmacias", min: 100, max: 350 },
    { clave: "lineGap", etiqueta: "Espacio entre líneas", min: 1, max: 15 },
    { clave: "pharmacyGap", etiqueta: "Espacio entre farmacias", min: 5, max: 45 },
  ],
  vertical: [
    { clave: "vStartX", etiqueta: "Margen izquierdo", min: 10, max: 150 },
    { clave: "vColWidth", etiqueta: "Ancho de columna", min: 100, max: 350 },
    { clave: "vColGap", etiqueta: "Separación de columnas", min: -20, max: 80 },
    { clave: "vHeaderBoxBottomR1", etiqueta: "Altura de los días, fila 1", min: 400, max: 800 },
    { clave: "vHeaderBoxBottomR2", etiqueta: "Altura de los días, fila 2", min: 150, max: 500 },
    { clave: "vHeaderBoxHeight", etiqueta: "Alto del encabezado", min: 10, max: 100 },
    { clave: "vPharmacyBoxBottomR1", etiqueta: "Altura de las farmacias, fila 1", min: 150, max: 500 },
    { clave: "vPharmacyBoxBottomR2", etiqueta: "Altura de las farmacias, fila 2", min: 10, max: 300 },
    { clave: "vPharmacyBoxHeight", etiqueta: "Alto del recuadro de farmacias", min: 100, max: 450 },
  ],
};

interface EstadoPlantilla {
  meta: PlantillaMeta | null | undefined; // undefined: revisando
  subiendo: { nombre: string; avance: number } | null;
  error: { nombre: string; mensaje: string; archivo: File | null } | null;
}

const INICIAL: EstadoPlantilla = { meta: undefined, subiendo: null, error: null };

function tamano(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toLocaleString("es-AR", { maximumFractionDigits: 1 })} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function fechaCorta(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()} ${MON3[d.getMonth()]}`;
}

function PlantillaSlot({ orientacion, estado, onArchivo, onReintentar }: {
  orientacion: Orientacion;
  estado: EstadoPlantilla;
  onArchivo: (archivo: File) => void;
  onReintentar: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const elegir = () => input.current?.click();
  const soltar = (e: DragEvent) => {
    e.preventDefault();
    const archivo = e.dataTransfer.files?.[0];
    if (archivo) onArchivo(archivo);
  };
  const ic = `grid shrink-0 place-items-center rounded-[10px] border border-adm-border bg-adm-surface text-adm-t55 ${orientacion === "vertical" ? "h-11 w-8" : "h-10 w-10"}`;
  const fila = "flex items-center gap-3 rounded-xl border p-3";
  const textos = "flex min-w-0 flex-1 flex-col gap-0.5";
  const nombre = "truncate text-sm font-medium text-adm-text";

  let contenido;
  if (estado.subiendo) {
    contenido = (
      <div className={`${fila} border-adm-border bg-adm-card`}>
        <div className={ic}><Spinner /></div>
        <div className={textos}>
          <span className={nombre}>{estado.subiendo.nombre}</span>
          <span className="text-xs text-adm-t55">Subiendo… {estado.subiendo.avance} %</span>
        </div>
      </div>
    );
  } else if (estado.error) {
    contenido = (
      <div role="alert" className={`${fila} border-adm-rojo-borde bg-adm-rojo-suave`}>
        <div className={ic}><CloudOff {...ICON} className="text-adm-rojo" /></div>
        <div className={textos}>
          <span className={nombre}>{estado.error.nombre}</span>
          <span className="text-xs text-adm-rojo">{estado.error.mensaje}</span>
        </div>
        <Button size="sm" onClick={onReintentar}>Reintentar</Button>
      </div>
    );
  } else if (estado.meta === undefined) {
    contenido = <Skeleton className="h-16" />;
  } else if (estado.meta) {
    contenido = (
      <div className={`${fila} border-adm-border bg-adm-card`}>
        <div className={ic}><FileText {...ICON} /></div>
        <div className={textos}>
          <span className={nombre}>{estado.meta.nombre}</span>
          <span className="flex flex-wrap items-center gap-1.5 text-xs text-adm-t55">
            <span className="inline-flex items-center gap-1 text-adm-acento-texto"><CloudCheck size={13} />Guardada</span>
            · {tamano(estado.meta.bytes)} · {fechaCorta(estado.meta.actualizada)}
          </span>
        </div>
        <Button size="sm" onClick={elegir}>Reemplazar</Button>
      </div>
    );
  } else {
    contenido = (
      <button
        type="button"
        onClick={elegir}
        onDragOver={e => e.preventDefault()}
        onDrop={soltar}
        className="flex flex-col items-center gap-1 rounded-xl border border-dashed border-adm-border-2 bg-adm-surface p-4 text-center text-[13px] text-adm-t55 hover:border-adm-acento hover:text-adm-text"
      >
        <Upload {...ICON} />
        <b className="font-medium text-adm-text">Subí la plantilla {orientacion}</b>
        <span>PDF de una página, hasta 20 MB</span>
      </button>
    );
  }

  return (
    <>
      <input
        ref={input}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={e => {
          const archivo = e.target.files?.[0];
          e.target.value = "";
          if (archivo) onArchivo(archivo);
        }}
      />
      {contenido}
    </>
  );
}

/** Campo de tamaño de letra: deja borrar y reescribir; guarda cuando el número es válido. */
function NumeroLetra({ id, valor, min, max, onCambio }: { id: string; valor: number; min: number; max: number; onCambio: (n: number) => void }) {
  const [texto, setTexto] = useState(String(valor));
  useEffect(() => setTexto(String(valor)), [valor]);
  return (
    <Input
      id={id}
      type="number"
      inputMode="numeric"
      min={min}
      max={max}
      value={texto}
      onChange={e => {
        setTexto(e.target.value);
        const n = parseInt(e.target.value);
        if (n >= min && n <= max) onCambio(n);
      }}
      onBlur={() => setTexto(String(valor))}
    />
  );
}

function Deslizador({ etiqueta, min, max, valor, onCambio }: { etiqueta: string; min: number; max: number; valor: number; onCambio: (n: number) => void }) {
  const id = useId();
  return (
    <div className="grid grid-cols-[1fr_40px] items-center gap-x-3 gap-y-1 py-1.5 text-[13px] text-adm-t70 md:grid-cols-[1fr_160px_40px]">
      <label htmlFor={id}>{etiqueta}</label>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        value={valor || 0}
        onChange={e => onCambio(parseInt(e.target.value))}
        className="col-span-2 row-start-2 w-full accent-adm-acento md:col-span-1 md:row-start-auto"
      />
      <code className="text-right font-mono text-xs text-adm-t55">{valor}</code>
    </div>
  );
}

export default function PlantillasPage() {
  const toast = useToast();
  const [plantillas, setPlantillas] = useState<Record<Orientacion, EstadoPlantilla>>({ horizontal: INICIAL, vertical: INICIAL });
  const [preset, setPreset] = useState<PDFPreset>(DEFAULT_PRESET);
  const guardarTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [ajustes, setAjustes] = useState<Orientacion>("horizontal");
  const [confirmarReinicio, setConfirmarReinicio] = useState(false);

  const cambiar = (o: Orientacion, cambio: Partial<EstadoPlantilla>) =>
    setPlantillas(prev => ({ ...prev, [o]: { ...prev[o], ...cambio } }));

  const leerMeta = async (o: Orientacion) => {
    cambiar(o, { meta: undefined, error: null });
    try {
      cambiar(o, { meta: await leerMetaPlantilla(o) });
    } catch (e) {
      cambiar(o, { error: { nombre: `Plantilla ${o}`, mensaje: `No se pudo revisar. ${motivoDeFalla(e)}`, archivo: null } });
    }
  };

  useEffect(() => {
    leerMeta("horizontal");
    leerMeta("vertical");
    cargarPreset()
      .then(setPreset)
      .catch(e => toast.show(`No se pudieron traer los ajustes. ${motivoDeFalla(e)}`, "error"));
  }, []);

  // El tilde («Guardada») aparece solo con la respuesta OK del servidor (§1.5)
  const subir = async (o: Orientacion, archivo: File) => {
    cambiar(o, { error: null, subiendo: { nombre: archivo.name, avance: 0 } });
    try {
      const meta = await subirPlantilla(o, archivo, avance => cambiar(o, { subiendo: { nombre: archivo.name, avance } }));
      cambiar(o, { meta, subiendo: null });
    } catch (e) {
      cambiar(o, { subiendo: null, error: { nombre: archivo.name, mensaje: `No se guardó. ${motivoDeFalla(e, "subir")}`, archivo } });
    }
  };

  const reintentar = (o: Orientacion) => {
    const archivo = plantillas[o].error?.archivo;
    if (archivo) subir(o, archivo);
    else leerMeta(o);
  };

  const aplicar = (nuevo: PDFPreset) => {
    setPreset(nuevo);
    if (guardarTimer.current) clearTimeout(guardarTimer.current);
    guardarTimer.current = setTimeout(() => {
      guardarPreset(nuevo).catch(e => toast.show(`No se guardaron los ajustes. ${motivoDeFalla(e)}`, "error"));
    }, 600);
  };
  const actualizar = (cambios: Partial<PDFPreset>) => aplicar({ ...preset, ...cambios });

  return (
    <>
      <div className="flex flex-col gap-4">
      <Card className="flex flex-col gap-[18px] p-4 md:p-6">
        <div className="flex flex-col gap-0.5">
          <div className="text-[15px] font-semibold text-adm-text">Plantillas</div>
          <div className="text-[13px] leading-normal text-adm-t55">El PDF de fondo de cada cartel. Se guarda en la nube; el tilde aparece recién cuando quedó guardado.</div>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {ORIENTACIONES.map(o => (
            <div key={o.value} className="flex flex-col gap-2">
              <span className="text-xs font-medium text-adm-t55">{o.label}</span>
              <PlantillaSlot
                orientacion={o.value}
                estado={plantillas[o.value]}
                onArchivo={archivo => subir(o.value, archivo)}
                onReintentar={() => reintentar(o.value)}
              />
            </div>
          ))}
        </div>
      </Card>

      <Card className="flex flex-col gap-[18px] p-4 md:p-6">
        <div className="flex flex-col gap-0.5">
          <div className="text-[15px] font-semibold text-adm-text">Letras</div>
          <div className="text-[13px] leading-normal text-adm-t55">Tamaño de partida; se achican solas si un día tiene muchas farmacias.</div>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Field label="Día" htmlFor="letra-dia">
            <NumeroLetra id="letra-dia" valor={preset.dayFontSize} min={10} max={60} onCambio={n => actualizar({ dayFontSize: n })} />
          </Field>
          <Field label="Nombre de la farmacia" htmlFor="letra-nombre">
            <NumeroLetra id="letra-nombre" valor={preset.nameFontSize} min={8} max={40} onCambio={n => actualizar({ nameFontSize: n })} />
          </Field>
          <Field label="Dirección y teléfono" htmlFor="letra-info">
            <NumeroLetra id="letra-info" valor={preset.infoFontSize} min={6} max={30} onCambio={n => actualizar({ infoFontSize: n })} />
          </Field>
        </div>
        <details className="group border-t border-adm-border pt-3">
          <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between text-sm font-medium text-adm-t70 md:min-h-9 [&::-webkit-details-marker]:hidden">
            Ajustes finos de posición
            <ChevronDown {...ICON} className="transition-transform group-open:rotate-180" />
          </summary>
          <div className="pt-2">
            <div className="mb-2">
              <Seg label="Orientación de los ajustes" options={ORIENTACIONES} value={ajustes} onChange={setAjustes} />
            </div>
            {AJUSTES[ajustes].map(a => (
              <Fragment key={a.clave}>
                <Deslizador
                  etiqueta={a.etiqueta}
                  min={a.min}
                  max={a.max}
                  valor={preset[a.clave] as number}
                  onCambio={n => actualizar({ [a.clave]: n } as Partial<PDFPreset>)}
                />
              </Fragment>
            ))}
            <div className="pt-2.5">
              <Button size="sm" onClick={() => setConfirmarReinicio(true)}>Volver a los predeterminados</Button>
            </div>
          </div>
        </details>
      </Card>

      </div>

      <Modal
        size="sm"
        open={confirmarReinicio}
        onClose={() => setConfirmarReinicio(false)}
        title="¿Volver a los predeterminados?"
        footer={
          <>
            <Button onClick={() => setConfirmarReinicio(false)}>Cancelar</Button>
            <Button
              variant="primary"
              onClick={() => {
                aplicar(DEFAULT_PRESET);
                setConfirmarReinicio(false);
              }}
            >
              Volver a los predeterminados
            </Button>
          </>
        }
      >
        <p className="text-[13px] text-adm-t55">Las letras y las posiciones vuelven a como venían, en horizontal y en vertical.</p>
      </Modal>
    </>
  );
}
